/* eslint-disable */
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { MagnifyingGlass, MapPin, Car, CalendarBlank, Clock, PaperPlaneTilt, CheckCircle, ArrowLeft, X } from "@phosphor-icons/react";
import { api } from "../../lib/api";

/**
 * Agent Booking Wizard — replaces the legacy booking dialog.
 * 4 steps:
 *   1. Pickup search (RodBez autocomplete)
 *   2. Drop search (RodBez autocomplete)
 *   3. Fare estimate (RodBez fare API → 3 vehicle types)
 *   4. Date / time → send quotation to customer
 *
 * UI behaviour (per user rule 2026-02):
 *   - Renders as a NON-MODAL right-side drawer so the chat stays
 *     readable + scrollable while the agent is filling the form.
 *   - All step state is persisted in localStorage keyed by phone, so
 *     closing the panel without finishing keeps the agent's progress.
 *   - State is cleared ONLY when the agent clicks the × button OR
 *     successfully sends the quotation.
 */

const STORAGE_KEY_PREFIX = "rb_booking_wizard_v1::";
function storageKey(phone) {
  return phone ? `${STORAGE_KEY_PREFIX}${phone}` : null;
}
function loadCached(phone) {
  try {
    const raw = localStorage.getItem(storageKey(phone));
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}
function saveCached(phone, data) {
  try {
    if (!phone) return;
    localStorage.setItem(storageKey(phone), JSON.stringify(data));
  } catch (_) {}
}
function clearCached(phone) {
  try {
    localStorage.removeItem(storageKey(phone));
  } catch (_) {}
}

function todayStr() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function nowPlusHourStr() {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

function PlaceSearch({ label, value, onPick, testidPrefix }) {
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!term || term.trim().length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await api.get("/agent-booking/autocomplete", { params: { term: term.trim() } });
        if (!cancelled) setResults(r.data?.results || []);
      } catch (e) {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setBusy(false);
      }
    }, 280);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [term]);

  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold flex items-center gap-1.5">
        <MapPin size={12} weight="fill" /> {label}
      </Label>
      {value ? (
        <div
          className="flex items-start gap-2 px-3 py-2 rounded-md border-2 border-[#10B981]/40 bg-[#10B981]/5"
          data-testid={`${testidPrefix}-selected`}
        >
          <CheckCircle size={14} weight="fill" className="text-[#10B981] mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold truncate">{value.name || value.formattedAddress || value.address}</div>
            {(() => {
              const addr = value.formattedAddress || value.address || "";
              if (addr && addr !== value.name) {
                return <div className="text-[11px] text-[var(--muted)] truncate">{addr}</div>;
              }
              return null;
            })()}
          </div>
          <Button
            type="button" variant="ghost" size="sm"
            className="h-6 px-2 text-[11px] shrink-0"
            onClick={() => { onPick(null); setTerm(""); setOpen(true); }}
            data-testid={`${testidPrefix}-change`}
          >Change</Button>
        </div>
      ) : (
        <div className="relative">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
            <Input
              value={term}
              onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              placeholder={`Search ${label.toLowerCase()}…`}
              className="pl-8 h-9"
              data-testid={`${testidPrefix}-input`}
            />
          </div>
          {open && term.trim().length >= 2 && (
            <div className="absolute z-50 mt-1 left-0 right-0 max-h-64 overflow-y-auto rounded-md border bg-[var(--bg)] shadow-lg">
              {busy && (
                <div className="px-3 py-2 text-xs text-[var(--muted)]">Searching…</div>
              )}
              {!busy && results.length === 0 && (
                <div className="px-3 py-2 text-xs text-[var(--muted)]">No matches</div>
              )}
              {!busy && results.map((r, idx) => {
                const fullAddr = r.formattedAddress || r.address || "";
                const showAddr = fullAddr && fullAddr !== r.name;
                return (
                  <button
                    key={`${r.placeId}-${idx}`}
                    type="button"
                    onClick={() => { onPick(r); setOpen(false); setTerm(""); }}
                    className="w-full text-left px-3 py-2 hover:bg-[var(--accent)] border-b last:border-b-0 transition-colors"
                    data-testid={`${testidPrefix}-row-${idx}`}
                  >
                    <div className="text-sm font-medium truncate">{r.name || fullAddr || "Unknown"}</div>
                    {showAddr && (
                      <div className="text-[11px] text-[var(--muted)] line-clamp-2">{fullAddr}</div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AgentBookingWizard({ open, onClose, contact }) {
  const [step, setStep] = useState(1); // 1=pickup, 2=drop, 3=fare, 4=datetime
  const [pickup, setPickup] = useState(null);
  const [drop, setDrop] = useState(null);
  const [fareData, setFareData] = useState(null); // {totalDistanceInKm, totalDurationInHours, vehicleTypes}
  const [fareLoading, setFareLoading] = useState(false);
  const [fareError, setFareError] = useState("");
  const [travelDate, setTravelDate] = useState(todayStr());
  const [travelTime, setTravelTime] = useState(nowPlusHourStr());
  const [customerName, setCustomerName] = useState(contact?.name || "");
  const [sending, setSending] = useState(false);
  // Tracks when the panel is HIDING (close without × — wizard stays in
  // cache for next time). Set to true only on explicit cross click.
  const explicitCloseRef = useRef(false);

  // Load cached state on open (per-phone). If no cache, start fresh.
  useEffect(() => {
    if (!open || !contact?.phone) return;
    explicitCloseRef.current = false;
    const cached = loadCached(contact.phone);
    if (cached) {
      setStep(cached.step || 1);
      setPickup(cached.pickup || null);
      setDrop(cached.drop || null);
      setFareData(cached.fareData || null);
      setTravelDate(cached.travelDate || todayStr());
      setTravelTime(cached.travelTime || nowPlusHourStr());
      setCustomerName(cached.customerName || contact?.name || "");
      setFareError("");
    } else {
      setStep(1);
      setPickup(null);
      setDrop(null);
      setFareData(null);
      setFareError("");
      setTravelDate(todayStr());
      setTravelTime(nowPlusHourStr());
      setCustomerName(contact?.name || "");
    }
  }, [open, contact?.phone]);

  // Persist on every meaningful change (per-phone, even mid-flow)
  useEffect(() => {
    if (!open || !contact?.phone) return;
    saveCached(contact.phone, {
      step, pickup, drop, fareData, travelDate, travelTime, customerName,
      savedAt: new Date().toISOString(),
    });
  }, [open, contact?.phone, step, pickup, drop, fareData, travelDate, travelTime, customerName]);

  // Auto-fetch fare when both legs picked & we land on step 3
  useEffect(() => {
    if (step !== 3 || !pickup || !drop) return;
    let cancelled = false;
    (async () => {
      setFareLoading(true);
      setFareError("");
      try {
        const r = await api.post("/agent-booking/fare", {
          sourcePlaceId: pickup.placeId,
          destinationPlaceId: drop.placeId,
        });
        if (!cancelled) setFareData(r.data || null);
      } catch (e) {
        if (!cancelled) {
          const msg = e?.response?.data?.detail || e?.message || "Fare lookup failed";
          setFareError(msg);
          setFareData(null);
        }
      } finally {
        if (!cancelled) setFareLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [step, pickup?.placeId, drop?.placeId]);

  const vehicles = useMemo(() => (fareData?.vehicleTypes || []).slice(0, 3), [fareData]);

  const sendQuote = async () => {
    if (!contact?.phone) {
      toast.error("Contact phone missing");
      return;
    }
    if (!pickup || !drop || !vehicles.length) {
      toast.error("Pickup, drop, aur fare options chahiye");
      return;
    }
    setSending(true);
    try {
      const fare_options = vehicles.map((v) => ({
        cityServiceId: v.cityServiceId,
        serviceName: v.serviceName,
        baseFare: v.baseFare,
        capacityHint: [v.passengers ? `${v.passengers} pax` : null, v.luggage ? `${v.luggage} bags` : null].filter(Boolean).join(", "),
      }));
      const r = await api.post("/agent-booking/quote", {
        phone: contact.phone,
        source: { placeId: pickup.placeId, name: pickup.name, address: pickup.formattedAddress || pickup.address },
        destination: { placeId: drop.placeId, name: drop.name, address: drop.formattedAddress || drop.address },
        fare_options,
        travel_date: travelDate,
        travel_time: travelTime,
        distance_km: fareData?.totalDistanceInKm,
        duration: fareData?.totalDurationInHours,
        customer_name: customerName.trim(),
      });
      toast.success("Quotation sent! 🚖", {
        description: `Customer ko ${fare_options.length} cab options bheje. Tap karte hi booking confirm ho jayegi.`,
      });
      // Successful send → clear the cache so next booking starts fresh
      clearCached(contact?.phone);
      explicitCloseRef.current = true;
      onClose?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Send failed";
      toast.error("Send failed", { description: String(msg) });
    } finally {
      setSending(false);
    }
  };

  const stepLabel = ["Pickup", "Drop", "Fare", "Schedule"][step - 1];

  // Explicit "×" — clears cache + closes
  const handleExplicitClose = () => {
    clearCached(contact?.phone);
    explicitCloseRef.current = true;
    onClose?.();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-y-0 right-0 z-40 w-full sm:w-[420px] flex flex-col bg-[var(--surface)] border-l border-[var(--border)] shadow-2xl animate-in slide-in-from-right duration-200"
      data-testid="agent-booking-wizard"
      // Prevent the panel itself from closing when chat clicks behind it.
      // No backdrop overlay — chat remains fully clickable & scrollable.
    >
      <div className="px-4 py-3 border-b border-[var(--border)] flex items-center gap-2">
        <Car size={18} weight="fill" className="text-[#25D366] shrink-0" />
        <div className="font-semibold text-sm">Send Booking Quotation</div>
        <span className="ml-auto text-[11px] font-normal text-[var(--muted)]">
          Step {step}/4 · {stepLabel}
        </span>
        <button
          type="button"
          onClick={handleExplicitClose}
          className="p-1 rounded hover:bg-[var(--accent)] text-[var(--muted)] hover:text-[var(--fg)]"
          data-testid="wizard-close-btn"
          title="Close & clear progress"
        >
          <X size={16} weight="bold" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {/* Progress bar */}
        <div className="flex gap-1 mb-1">
          {[1,2,3,4].map((s) => (
            <div
              key={s}
              className={`h-1 flex-1 rounded-full transition-colors ${
                s <= step ? "bg-[#25D366]" : "bg-[var(--border)]"
              }`}
            />
          ))}
        </div>

        <div className="text-[11px] text-[var(--muted)] mb-2">
          Customer: <span className="font-mono">{contact?.phone || "?"}</span>
          {contact?.name && <> · {contact.name}</>}
        </div>

        {/* Step 1: Pickup */}
        {step === 1 && (
          <PlaceSearch
            label="Pickup location"
            value={pickup}
            onPick={(p) => { setPickup(p); if (p) setStep(2); }}
            testidPrefix="pickup"
          />
        )}

        {/* Step 2: Drop */}
        {step === 2 && (
          <div className="space-y-3">
            <div className="text-[11px] text-[var(--muted)]">
              Pickup: <span className="font-medium text-[var(--fg)]">{pickup?.name || pickup?.address}</span>
            </div>
            <PlaceSearch
              label="Drop location"
              value={drop}
              onPick={(p) => { setDrop(p); if (p) setStep(3); }}
              testidPrefix="drop"
            />
          </div>
        )}

        {/* Step 3: Fare */}
        {step === 3 && (
          <div className="space-y-3">
            <div className="text-[11px] text-[var(--muted)]">
              <div>Pickup: <span className="font-medium text-[var(--fg)]">{pickup?.name}</span></div>
              <div>Drop: <span className="font-medium text-[var(--fg)]">{drop?.name}</span></div>
            </div>
            {fareLoading && (
              <div className="py-6 text-center text-sm text-[var(--muted)]">
                Calculating fares from RodBez…
              </div>
            )}
            {fareError && !fareLoading && (
              <div className="px-3 py-3 rounded-md border border-[#EF4444]/40 bg-[#EF4444]/10 text-[#991B1B] dark:text-[#FCA5A5] text-xs">
                {fareError}
              </div>
            )}
            {!fareLoading && fareData && (
              <>
                <div className="flex gap-2 text-[11px] text-[var(--muted)]">
                  <span>📏 {fareData.totalDistanceInKm} km</span>
                  <span>⏱️ {fareData.totalDurationInHours}</span>
                </div>
                <div className="space-y-2" data-testid="fare-options">
                  {vehicles.map((v, idx) => (
                    <div
                      key={v.cityServiceId}
                      className="flex items-center gap-3 px-3 py-2 rounded-md border bg-[var(--accent)]"
                      data-testid={`fare-row-${idx}`}
                    >
                      <Car size={20} weight="fill" className="text-[#25D366] shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold">{v.serviceName}</div>
                        <div className="text-[11px] text-[var(--muted)]">
                          {v.passengers ? `${v.passengers} pax` : ""}
                          {v.luggage ? ` · ${v.luggage} bags` : ""}
                        </div>
                      </div>
                      <div className="text-base font-bold">₹{Number(v.baseFare || 0).toLocaleString("en-IN")}</div>
                    </div>
                  ))}
                </div>
                {!vehicles.length && (
                  <div className="text-xs text-[var(--muted)] italic">
                    No vehicle options returned by RodBez API.
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Step 4: Date / Time / Send */}
        {step === 4 && (
          <div className="space-y-3">
            <div className="text-[11px] text-[var(--muted)]">
              <div>{pickup?.name} → {drop?.name}</div>
              <div>{vehicles.length} cab options · ₹{Number(vehicles[0]?.baseFare||0).toLocaleString("en-IN")}–₹{Number(vehicles[vehicles.length-1]?.baseFare||0).toLocaleString("en-IN")}</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold flex items-center gap-1.5">
                  <CalendarBlank size={12} weight="fill" /> Travel date
                </Label>
                <Input
                  type="date"
                  value={travelDate}
                  min={todayStr()}
                  onChange={(e) => setTravelDate(e.target.value)}
                  className="h-9"
                  data-testid="travel-date-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold flex items-center gap-1.5">
                  <Clock size={12} weight="fill" /> Travel time
                </Label>
                <Input
                  type="time"
                  value={travelTime}
                  onChange={(e) => setTravelTime(e.target.value)}
                  className="h-9"
                  data-testid="travel-time-input"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Customer name (optional)</Label>
              <Input
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Customer ka naam (booking record me jayega)"
                className="h-9"
                data-testid="customer-name-input"
              />
            </div>
            <div className="px-3 py-2 rounded-md bg-[#10B981]/10 border border-[#10B981]/30 text-[11px] text-[#065F46] dark:text-[#6EE7B7]">
              💡 Send karte hi customer ko WhatsApp pe quotation milega — vo apna preferred cab tap karke instantly book kar sakta hai.
            </div>
          </div>
        )}

        {/* Footer nav */}
        <div className="flex items-center gap-2 pt-2 border-t mt-2">
          {step > 1 && (
            <Button
              type="button" variant="ghost" size="sm"
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              data-testid="wizard-back-btn"
            >
              <ArrowLeft size={14} className="mr-1" /> Back
            </Button>
          )}
          <div className="ml-auto flex gap-2">
            {step === 3 && (
              <Button
                type="button"
                size="sm"
                onClick={() => setStep(4)}
                disabled={fareLoading || !!fareError || !vehicles.length}
                className="bg-[#25D366] hover:bg-[#1da851] text-white"
                data-testid="wizard-next-btn"
              >
                Continue →
              </Button>
            )}
            {step === 4 && (
              <Button
                type="button"
                size="sm"
                onClick={sendQuote}
                disabled={sending || !pickup || !drop || !vehicles.length || !travelDate || !travelTime}
                className="bg-[#25D366] hover:bg-[#1da851] text-white"
                data-testid="wizard-send-btn"
              >
                <PaperPlaneTilt size={14} weight="fill" className="mr-1" />
                {sending ? "Sending…" : "Send quotation"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
